import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, Zap, Sparkles, Gauge, Image, FileType, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EnhancementSettings {
  // Image Upscaling
  upscalingEnabled: boolean;
  upscalingAlgorithm: 'esrgan' | 'gemini-ai' | 'lanczos';
  upscalingScale: number;
  faceEnhance: boolean;
  // DPI Embedding
  dpiEmbeddingEnabled: boolean;
  targetDPI: number;
}

export const AdminAutomationDPISettings = () => {
  const [settings, setSettings] = useState<EnhancementSettings>({
    upscalingEnabled: false,
    upscalingAlgorithm: 'esrgan',
    upscalingScale: 4,
    faceEnhance: false,
    dpiEmbeddingEnabled: false,
    targetDPI: 300
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replicateStatus, setReplicateStatus] = useState<'connected' | 'not_configured' | 'checking'>('checking');

  // Load settings from site_configurations
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('site_configurations')
          .select('key, value')
          .in('key', [
            'automation_upscaling_enabled',
            'automation_upscaling_algorithm',
            'automation_upscaling_scale',
            'automation_face_enhance',
            'automation_dpi_embedding_enabled',
            'automation_dpi_target',
            // Legacy keys for migration
            'automation_dpi_enhancement_enabled',
            'automation_dpi_algorithm'
          ]);

        if (error) throw error;

        const configMap: Record<string, string> = {};
        data?.forEach(item => {
          configMap[item.key] = item.value;
        });

        // Support legacy keys ONLY as fallback when new keys don't exist
        const hasNewUpscalingKey = configMap['automation_upscaling_enabled'] !== undefined;
        const hasNewDpiKey = configMap['automation_dpi_embedding_enabled'] !== undefined;
        const legacyEnabled = configMap['automation_dpi_enhancement_enabled'] === 'true';
        const legacyAlgorithm = configMap['automation_dpi_algorithm'] as EnhancementSettings['upscalingAlgorithm'];

        setSettings({
          // Only use legacy fallback when new key doesn't exist at all
          upscalingEnabled: hasNewUpscalingKey 
            ? configMap['automation_upscaling_enabled'] === 'true' 
            : legacyEnabled,
          upscalingAlgorithm: (configMap['automation_upscaling_algorithm'] as EnhancementSettings['upscalingAlgorithm']) || legacyAlgorithm || 'esrgan',
          upscalingScale: parseInt(configMap['automation_upscaling_scale'] || '4'),
          faceEnhance: configMap['automation_face_enhance'] === 'true',
          dpiEmbeddingEnabled: hasNewDpiKey 
            ? configMap['automation_dpi_embedding_enabled'] === 'true' 
            : legacyEnabled,
          targetDPI: parseInt(configMap['automation_dpi_target'] || '300')
        });
      } catch (error) {
        console.error('Failed to load enhancement settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
    checkReplicateConnection();
  }, []);

  const checkReplicateConnection = async () => {
    setReplicateStatus('checking');
    try {
      const { data, error } = await supabase.functions.invoke('upscale-image-esrgan', {
        body: { test: true }
      });
      
      if (error?.message?.includes('REPLICATE_API_KEY')) {
        setReplicateStatus('not_configured');
      } else {
        setReplicateStatus('connected');
      }
    } catch (e) {
      setReplicateStatus('connected');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updates = [
        { key: 'automation_upscaling_enabled', value: settings.upscalingEnabled.toString() },
        { key: 'automation_upscaling_algorithm', value: settings.upscalingAlgorithm },
        { key: 'automation_upscaling_scale', value: settings.upscalingScale.toString() },
        { key: 'automation_face_enhance', value: settings.faceEnhance.toString() },
        { key: 'automation_dpi_embedding_enabled', value: settings.dpiEmbeddingEnabled.toString() },
        { key: 'automation_dpi_target', value: settings.targetDPI.toString() }
      ];

      console.log('Saving enhancement settings:', settings);

      for (const update of updates) {
        const { data: existing } = await supabase
          .from('site_configurations')
          .select('id')
          .eq('key', update.key)
          .maybeSingle();

        if (existing) {
          // Use .select() to return updated row and verify it worked
          const { data, error } = await supabase
            .from('site_configurations')
            .update({
              value: update.value,
              updated_at: new Date().toISOString()
            })
            .eq('key', update.key)
            .select('id, key, value')
            .single();

          if (error) throw error;
          
          // Verify the value was actually updated
          if (!data || data.value !== update.value) {
            console.error(`Update verification failed for ${update.key}:`, { expected: update.value, got: data?.value });
            throw new Error(`Failed to update ${update.key} - RLS may be blocking the update`);
          }
          
          console.log(`Updated ${update.key}:`, data.value);
        } else {
          // Insert new config with verification
          const { data, error } = await supabase
            .from('site_configurations')
            .insert({
              key: update.key,
              value: update.value,
              display_name: getDisplayName(update.key),
              description: getDescription(update.key),
              category: 'image_generation' as const,
              data_type: getDataType(update.key) as 'boolean' | 'number' | 'string',
            })
            .select('id, key, value')
            .single();

          if (error) throw error;
          
          if (!data) {
            throw new Error(`Failed to insert ${update.key}`);
          }
          
          console.log(`Inserted ${update.key}:`, data.value);
        }
      }

      // Re-fetch critical settings to confirm persistence
      const { data: verification, error: verifyError } = await supabase
        .from('site_configurations')
        .select('key, value')
        .in('key', ['automation_upscaling_enabled', 'automation_dpi_embedding_enabled']);

      if (verifyError) {
        console.error('Verification fetch failed:', verifyError);
      } else {
        const verifyMap = Object.fromEntries(verification?.map(c => [c.key, c.value]) || []);
        console.log('Verification result:', verifyMap);
        
        const expectedUpscaling = settings.upscalingEnabled.toString();
        const expectedDpi = settings.dpiEmbeddingEnabled.toString();
        
        if (verifyMap['automation_upscaling_enabled'] !== expectedUpscaling) {
          throw new Error(`Upscaling setting did not persist (expected ${expectedUpscaling}, got ${verifyMap['automation_upscaling_enabled']})`);
        }
        if (verifyMap['automation_dpi_embedding_enabled'] !== expectedDpi) {
          throw new Error(`DPI setting did not persist (expected ${expectedDpi}, got ${verifyMap['automation_dpi_embedding_enabled']})`);
        }
      }

      toast.success('Enhancement settings saved and verified');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(`Failed to save settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const getDisplayName = (key: string): string => {
    const names: Record<string, string> = {
      'automation_upscaling_enabled': 'Enable Upscaling',
      'automation_upscaling_algorithm': 'Upscaling Algorithm',
      'automation_upscaling_scale': 'Upscaling Scale',
      'automation_face_enhance': 'Face Enhancement',
      'automation_dpi_embedding_enabled': 'Enable DPI Embedding',
      'automation_dpi_target': 'Target DPI'
    };
    return names[key] || key;
  };

  const getDescription = (key: string): string => {
    const descriptions: Record<string, string> = {
      'automation_upscaling_enabled': 'Enable image upscaling after generation',
      'automation_upscaling_algorithm': 'Algorithm for image upscaling (esrgan, gemini-ai, lanczos)',
      'automation_upscaling_scale': 'Scale factor for upscaling (2x, 4x, 8x)',
      'automation_face_enhance': 'Enable face enhancement for portrait images',
      'automation_dpi_embedding_enabled': 'Enable DPI metadata embedding',
      'automation_dpi_target': 'Target DPI for print-ready output (150, 300, 600)'
    };
    return descriptions[key] || '';
  };

  const getDataType = (key: string): string => {
    if (key.includes('enabled') || key.includes('enhance')) return 'boolean';
    if (key.includes('scale') || key.includes('target')) return 'number';
    return 'string';
  };

  const getAlgorithmInfo = (algo: string) => {
    switch (algo) {
      case 'esrgan':
        return {
          name: 'Real-ESRGAN',
          description: 'Industry best quality, ~$0.002/image',
          icon: Sparkles,
          badge: 'Best Quality',
          badgeVariant: 'default' as const
        };
      case 'gemini-ai':
        return {
          name: 'Gemini AI',
          description: 'Good quality, uses AI credits',
          icon: Zap,
          badge: 'Standard',
          badgeVariant: 'secondary' as const
        };
      case 'lanczos':
        return {
          name: 'Lanczos',
          description: 'Free, fast, algorithmic upscaling',
          icon: Gauge,
          badge: 'Free',
          badgeVariant: 'outline' as const
        };
      default:
        return {
          name: algo,
          description: '',
          icon: Zap,
          badge: '',
          badgeVariant: 'secondary' as const
        };
    }
  };

  if (loading) {
    return (
      <Card className="bg-background/40 backdrop-blur-md border-border/50">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const algoInfo = getAlgorithmInfo(settings.upscalingAlgorithm);

  // Build processing flow description
  const getProcessingFlow = () => {
    const steps = ['Generate'];
    if (settings.upscalingEnabled) {
      steps.push(`Upscale (${settings.upscalingScale}x ${settings.upscalingAlgorithm.toUpperCase()})`);
    }
    if (settings.dpiEmbeddingEnabled) {
      steps.push(`Embed DPI (${settings.targetDPI})`);
    }
    steps.push('Save');
    return steps;
  };

  return (
    <div className="space-y-6">
      {/* Image Upscaling Section */}
      <Card className="bg-background/40 backdrop-blur-md border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5 text-primary" />
                Image Upscaling (Enhancement)
              </CardTitle>
              <CardDescription>
                Upscale images to higher resolution after generation
              </CardDescription>
            </div>
            {settings.upscalingAlgorithm === 'esrgan' && (
              <div className="flex items-center gap-2">
                {replicateStatus === 'checking' && (
                  <Badge variant="outline" className="gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Checking...
                  </Badge>
                )}
                {replicateStatus === 'connected' && (
                  <Badge variant="default" className="gap-1 bg-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Replicate Connected
                  </Badge>
                )}
                {replicateStatus === 'not_configured' && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    API Key Required
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Upscaling Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
            <div className="space-y-0.5">
              <Label className="text-base">Enable Image Upscaling</Label>
              <p className="text-sm text-muted-foreground">
                Upscale images to higher resolution after generation
              </p>
            </div>
            <Switch
              checked={settings.upscalingEnabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, upscalingEnabled: checked }))}
            />
          </div>

          {settings.upscalingEnabled && (
            <>
              {/* Algorithm Selection */}
              <div className="space-y-3">
                <Label>Upscaling Algorithm</Label>
                <Select 
                  value={settings.upscalingAlgorithm} 
                  onValueChange={(value: EnhancementSettings['upscalingAlgorithm']) => setSettings(prev => ({ ...prev, upscalingAlgorithm: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="esrgan">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        <span>Real-ESRGAN (Best Quality)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="gemini-ai">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        <span>Gemini AI (Standard)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="lanczos">
                      <div className="flex items-center gap-2">
                        <Gauge className="h-4 w-4" />
                        <span>Lanczos (Free/Fast)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Algorithm Info Card */}
                <div className="rounded-lg border border-border/50 bg-muted/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <algoInfo.icon className="h-4 w-4 text-primary" />
                    <span className="font-medium">{algoInfo.name}</span>
                    <Badge variant={algoInfo.badgeVariant}>{algoInfo.badge}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{algoInfo.description}</p>
                </div>
              </div>

              {/* Scale Selection */}
              <div className="space-y-3">
                <Label>Upscaling Scale</Label>
                <Select 
                  value={settings.upscalingScale.toString()} 
                  onValueChange={(value) => setSettings(prev => ({ ...prev, upscalingScale: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2x (Double resolution)</SelectItem>
                    <SelectItem value="4">4x (Quadruple resolution)</SelectItem>
                    <SelectItem value="8">8x (8x resolution - slower)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Face Enhancement Toggle */}
              <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Face Enhancement</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable GFPGAN face enhancement for portrait images (ESRGAN only)
                  </p>
                </div>
                <Switch
                  checked={settings.faceEnhance}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, faceEnhance: checked }))}
                  disabled={settings.upscalingAlgorithm !== 'esrgan'}
                />
              </div>

              {/* Warning for ESRGAN without API key */}
              {settings.upscalingAlgorithm === 'esrgan' && replicateStatus === 'not_configured' && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-500">Replicate API Key Required</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Real-ESRGAN requires a Replicate API key. Add the REPLICATE_API_KEY secret in Supabase or switch to Gemini AI / Lanczos.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* DPI Metadata Embedding Section */}
      <Card className="bg-background/40 backdrop-blur-md border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileType className="h-5 w-5 text-primary" />
            DPI Metadata Embedding
          </CardTitle>
          <CardDescription>
            Set DPI metadata for print-ready output (embedded in PNG/JPEG headers)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable DPI Embedding Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
            <div className="space-y-0.5">
              <Label className="text-base">Enable DPI Embedding</Label>
              <p className="text-sm text-muted-foreground">
                Embed DPI metadata into images for print applications
              </p>
            </div>
            <Switch
              checked={settings.dpiEmbeddingEnabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, dpiEmbeddingEnabled: checked }))}
            />
          </div>

          {settings.dpiEmbeddingEnabled && (
            <div className="space-y-3">
              <Label>Target DPI</Label>
              <Select 
                value={settings.targetDPI.toString()} 
                onValueChange={(value) => setSettings(prev => ({ ...prev, targetDPI: parseInt(value) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="150">
                    <span>150 DPI - Web/Screen Quality</span>
                  </SelectItem>
                  <SelectItem value="300">
                    <span>300 DPI - Print Ready (Standard)</span>
                  </SelectItem>
                  <SelectItem value="600">
                    <span>600 DPI - High Quality Print</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processing Flow Summary */}
      <Card className="bg-background/40 backdrop-blur-md border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📊 Processing Flow Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Visual Flow */}
          <div className="flex items-center gap-2 flex-wrap">
            {getProcessingFlow().map((step, index) => (
              <div key={step} className="flex items-center gap-2">
                <Badge 
                  variant={index === 0 || index === getProcessingFlow().length - 1 ? 'outline' : 'default'}
                  className={index > 0 && index < getProcessingFlow().length - 1 ? 'bg-primary/80' : ''}
                >
                  {step}
                </Badge>
                {index < getProcessingFlow().length - 1 && (
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>

          <Separator />

          {/* Status Summary */}
          <div className="grid gap-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Image Upscaling:</span>
              <Badge variant={settings.upscalingEnabled ? 'default' : 'secondary'}>
                {settings.upscalingEnabled 
                  ? `${settings.upscalingAlgorithm.toUpperCase()} ${settings.upscalingScale}x` 
                  : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">DPI Embedding:</span>
              <Badge variant={settings.dpiEmbeddingEnabled ? 'default' : 'secondary'}>
                {settings.dpiEmbeddingEnabled ? `${settings.targetDPI} DPI` : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Face Enhancement:</span>
              <Badge variant={settings.faceEnhance && settings.upscalingEnabled && settings.upscalingAlgorithm === 'esrgan' ? 'default' : 'secondary'}>
                {settings.faceEnhance && settings.upscalingEnabled && settings.upscalingAlgorithm === 'esrgan' ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </div>

          {/* Info Box */}
          {!settings.upscalingEnabled && !settings.dpiEmbeddingEnabled && (
            <div className="rounded-lg border border-muted bg-muted/30 p-3">
              <p className="text-sm text-muted-foreground">
                💡 No post-processing enabled. Images will be saved as-is after generation (fastest mode).
              </p>
            </div>
          )}

          {settings.upscalingEnabled && !settings.dpiEmbeddingEnabled && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
              <p className="text-sm text-muted-foreground">
                🖼️ <span className="text-blue-400 font-medium">Upscaling Only:</span> Images will be upscaled but no DPI metadata will be embedded.
              </p>
            </div>
          )}

          {!settings.upscalingEnabled && settings.dpiEmbeddingEnabled && (
            <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-3">
              <p className="text-sm text-muted-foreground">
                📐 <span className="text-green-400 font-medium">DPI Only:</span> Original size images with DPI metadata embedded for print applications.
              </p>
            </div>
          )}

          {settings.upscalingEnabled && settings.dpiEmbeddingEnabled && (
            <div className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-3">
              <p className="text-sm text-muted-foreground">
                ✨ <span className="text-purple-400 font-medium">Full Enhancement:</span> Images will be upscaled AND have DPI metadata embedded for best print quality.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button 
        onClick={saveSettings} 
        disabled={saving}
        className="w-full"
        size="lg"
      >
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          'Save Enhancement Settings'
        )}
      </Button>
    </div>
  );
};
